import React, { useState } from 'react';
import { PencilSquareIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import ConfirmationDialog from './ConfirmationDialog';
import { useAuth } from '../../context/AuthContext';

/**
 * Editable field component for inline editing
 */
const EditableField = ({ value, fieldName, shortName, placeholder, isNumeric = false, formatDisplay = null, onChangeSuccess = null, className = "" }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [fieldValue, setFieldValue] = useState(value || '');
  const [prevValue, setPrevValue] = useState(value || '');
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [permissionError, setPermissionError] = useState(false);
  
  // Handle field click to enter edit mode
  const handleEditClick = () => {
    setPrevValue(fieldValue); // Store current value in case we need to revert
    // For numeric fields, remove formatting when entering edit mode
    if (isNumeric && fieldValue) {
      setFieldValue(String(parseFloat(String(fieldValue).replace(/[$,]/g, ''))));
    }
    setIsEditing(true);
  };
  
  // Handle field changes
  const handleChange = (e) => {
    const newValue = e.target.value;
    // For numeric fields, only allow numbers and decimal point
    if (isNumeric) {
      // Only allow digits, decimal point, and negative sign at beginning
      if (/^-?\d*\.?\d*$/.test(newValue) || newValue === '') {
        setFieldValue(newValue);
      }
    } else {
      setFieldValue(newValue);
    }
  };
  
  // Handle finishing edit
  const handleFinishEdit = () => {
    // If value hasn't changed, just exit edit mode
    const prevValueStr = typeof prevValue === 'string' ? prevValue : String(prevValue || '');
    const currentValue = isNumeric ? parseFloat(prevValueStr.replace(/[$,]/g, '')) : prevValue;
    const newValue = isNumeric ? (fieldValue === '' ? 0 : parseFloat(fieldValue)) : fieldValue;
    
    if (String(currentValue) === String(newValue)) {
      setIsEditing(false);
      return;
    }
    
    // Show confirmation dialog
    setShowConfirmation(true);
  };
  
  // Handle saving the field value
  const handleConfirmSave = async () => {
    setShowConfirmation(false);
    setIsUpdating(true);
    setError(null);
    
    try {
      // Import is inside to avoid circular dependencies
      const { updateScopeField } = await import('../../utils/api');
      
      // For numeric fields, ensure we're sending a number
      const valueToSend = isNumeric 
        ? (fieldValue === '' ? 0 : parseFloat(fieldValue)) 
        : fieldValue;
      
      // Call the API to update the field
      const result = await updateScopeField(shortName, fieldName, valueToSend);
      
      if (result.success) {
        // For numeric fields, update the display with formatted value
        if (isNumeric && formatDisplay) {
          setFieldValue(valueToSend);
        }
        setIsEditing(false);
        
        // Call the success callback if provided
        if (onChangeSuccess && typeof onChangeSuccess === 'function') {
          onChangeSuccess();
        }
      } else {
        // Check if this is a permission error
        if (result.error && result.error.includes('permission')) {
          setPermissionError(true);
          setError("You don't have permission to edit this field");
          setTimeout(() => {
            setPermissionError(false);
            setError(null);
          }, 5000); // Auto-hide after 5 seconds
        } else {
          setError('Failed to update field');
        }
        setFieldValue(prevValue); // Revert to previous value
      }
    } catch (error) {
      console.error(`Error updating ${fieldName}:`, error);
      // Check for 403 Forbidden status (permission error)
      if (error.message && (error.message.includes('403') || error.message.toLowerCase().includes('permission'))) {
        setPermissionError(true);
        setError("You don't have permission to edit this field");
        setTimeout(() => {
          setPermissionError(false);
          setError(null);
        }, 5000); // Auto-hide after 5 seconds
      } else {
        setError(`Error: ${error.message}`);
      }
      setFieldValue(prevValue); // Revert to previous value
    } finally {
      setIsUpdating(false);
    }
  };
  
  // Handle canceling the edit
  const handleCancelEdit = () => {
    setShowConfirmation(false);
    setFieldValue(prevValue); // Revert to previous value
    setIsEditing(false);
  };
  
  // Handle key press events (Enter to save, Escape to cancel)
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault(); // Prevent default behavior
      
      // If value hasn't changed, just exit edit mode
      const prevValueStr = typeof prevValue === 'string' ? prevValue : String(prevValue || '');
      const currentValue = isNumeric ? parseFloat(prevValueStr.replace(/[$,]/g, '')) : prevValue;
      const newValue = isNumeric ? (fieldValue === '' ? 0 : parseFloat(fieldValue)) : fieldValue;
      
      if (String(currentValue) === String(newValue)) {
        setIsEditing(false);
        return;
      }
      
      // Save directly without confirmation on Enter key
      setIsUpdating(true);
      setError(null);
      
      // Immediately trigger save
      (async () => {
        try {
          // Import is inside to avoid circular dependencies
          const { updateScopeField } = await import('../../utils/api');
          
          // For numeric fields, ensure we're sending a number
          const valueToSend = isNumeric 
            ? (fieldValue === '' ? 0 : parseFloat(fieldValue)) 
            : fieldValue;
          
          // Call the API to update the field
          const result = await updateScopeField(shortName, fieldName, valueToSend);
          
          if (result.success) {
            // For numeric fields, update the display with formatted value
            if (isNumeric && formatDisplay) {
              setFieldValue(valueToSend);
            }
            setIsEditing(false);
            
            // Call the success callback if provided
            if (onChangeSuccess && typeof onChangeSuccess === 'function') {
              onChangeSuccess();
            }
          } else {
            // Check if this is a permission error
            if (result.error && result.error.includes('permission')) {
              setPermissionError(true);
              setError("You don't have permission to edit this field");
              setTimeout(() => {
                setPermissionError(false);
                setError(null);
              }, 5000); // Auto-hide after 5 seconds
            } else {
              setError('Failed to update field');
            }
            setFieldValue(prevValue); // Revert to previous value
          }
        } catch (error) {
          console.error(`Error updating ${fieldName}:`, error);
          // Check for 403 Forbidden status (permission error)
          if (error.message && (error.message.includes('403') || error.message.toLowerCase().includes('permission'))) {
            setPermissionError(true);
            setError("You don't have permission to edit this field");
            setTimeout(() => {
              setPermissionError(false);
              setError(null);
            }, 5000); // Auto-hide after 5 seconds
          } else {
            setError(`Error: ${error.message}`);
          }
          setFieldValue(prevValue); // Revert to previous value
        } finally {
          setIsUpdating(false);
        }
      })();
    } else if (e.key === 'Escape') {
      setFieldValue(prevValue); // Revert to previous value
      setIsEditing(false);
    }
  };
  
  if (isEditing) {
    return (
      <div className="relative">
        <input
          type={isNumeric ? "text" : "text"}
          value={fieldValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={(e) => {
            // Don't trigger onBlur when clicking the confirmation buttons
            if (showConfirmation) return;
            handleFinishEdit();
          }}
          className={`block w-full rounded-md bg-white px-3 py-1.5 text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6 ${className}`}
          placeholder={placeholder}
          disabled={isUpdating}
          autoFocus
        />
        {isUpdating && (
          <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
            <span className="text-xs text-gray-500 animate-pulse">Updating...</span>
          </div>
        )}
        {error && (
          <div className="mt-1 flex items-center">
            <ExclamationCircleIcon className="h-4 w-4 text-red-500 mr-1" aria-hidden="true" />
            <p className="text-xs text-red-500">{error}</p>
          </div>
        )}
        
        {/* Confirmation Dialog */}
        <ConfirmationDialog
          open={showConfirmation}
          onClose={() => setShowConfirmation(false)}
          oldValue={isNumeric && formatDisplay ? formatDisplay(typeof prevValue === 'string' ? prevValue : String(prevValue || '')) : prevValue}
          newValue={isNumeric && formatDisplay ? formatDisplay(fieldValue === '' ? 0 : parseFloat(fieldValue)) : fieldValue}
          fieldName={fieldName}
          onConfirm={handleConfirmSave}
          onCancel={handleCancelEdit}
        />
      </div>
    );
  }
  
  // Apply formatting for display if provided
  const displayValue = formatDisplay && fieldValue !== '' ? formatDisplay(fieldValue) : fieldValue;
  
  return (
    <div className="relative">
      <div className="group flex items-center">
        <span className={`${className || "text-gray-700"}`}>{displayValue || 'N/A'}</span>
        <button
          type="button"
          onClick={handleEditClick}
          className="ml-2 rounded-full p-1 text-gray-400 opacity-0 group-hover:opacity-100 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          title={`Edit ${fieldName}`}
        >
          <PencilSquareIcon className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
      
      {/* Inline error message */}
      {error && (
        <div className="mt-1 flex items-center">
          <ExclamationCircleIcon className="h-4 w-4 text-red-500 mr-1" aria-hidden="true" />
          <p className="text-xs text-red-500">{error}</p>
        </div>
      )}
    </div>
  );
};

export default EditableField;